import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TokenStorageService } from '../token-storage.service';

export const WS_ENDPOINT = `${environment.apiUrl.replace('http','ws')}/ws`;
export const WS_HANDSHAKE_ENDPOINT = `/dashboard/live-track`;

export const BIRD_EYE_MSG_DEFAULT = 'Bird\'s Eye View';
export const BIRD_EYE_MSG_NOT_LOGGED_IN = 'The driver needs to be logged in to activate Bird\'s Eye View and live vehicle tracking.';
export const BIRD_EYE_MSG_NOT_CONNECTED = 'The driver needs to be connected to activate Bird\'s Eye View and live vehicle tracking.';
export const BIRD_EYE_MSG_NOT_IN_MOTION = 'The vehicle needs to be in motion to activate Bird\'s Eye View and live vehicle tracking.';
export const BIRD_EYE_MSG_LATE_SYNCED = 'Last sync must be within the last five minutes to activate Bird\'s Eye View and live vehicle tracking.';
export const BIRD_EYE_MSG_WRONG_MOBILE_VERSION = 'This feature requires the driver to have at least version 1.3.0 of the RouteMate ELD mobile app. Please contact the driver to ensure the app is updated.';
export const BIRD_EYE_MSG_FAILED_CONNECT = 'Bird\'s Eye View is currently unavailable. We apologize for the inconvenience.';
export const BIRD_EYE_MSG_INACTIVE_SOCKET = 'Bird\'s Eye View is currently unavailable. We apologize for the inconvenience.';
export const BIRD_EYE_MSG_CONTINUE_CONFIRMATION = 'Do you want to continue with Bird\'s Eye View?';
export const BIRD_EYE_MSG_EMPTY_BUFFER = 'Buffer is empty, please wait for the next location update.';
export const BIRD_EYE_WS_UI_ERRORS_MAP: Map<string, string> = new Map([
  ['ERROR: Unsupported mobile version', BIRD_EYE_MSG_WRONG_MOBILE_VERSION],
  ['ERROR: Driver not logged in', BIRD_EYE_MSG_NOT_LOGGED_IN],
  ['ERROR: Driver not connected', BIRD_EYE_MSG_NOT_CONNECTED],
  ['ERROR: Driver disconnected', BIRD_EYE_MSG_NOT_CONNECTED],
  ['ERROR: Driver not driving', BIRD_EYE_MSG_NOT_IN_MOTION],
  ['ERROR: driver failed to connect', BIRD_EYE_MSG_FAILED_CONNECT],
  ['ERROR: inactive socket', BIRD_EYE_MSG_INACTIVE_SOCKET],
  ['WARNING: time excited', BIRD_EYE_MSG_CONTINUE_CONFIRMATION],
  ['WARNING: empty buffer', BIRD_EYE_MSG_EMPTY_BUFFER]
]);

export const BIRD_EYE_SOCKET_DELAY = 3000; //ms
export const BIRD_EYE_DELAY_OFFSET_FACTOR = 0.9;
export const BIRD_EYE_LOCATION_INTER_POINTS = 100;
export const BIRD_EYE_MOVE_TOLERANCE = 0.000003;
export const BIRD_EYE_BUFFER_ON = true;
export const BIRD_EYE_BUFFER_MIN_SIZE = 2;
export const BIRD_EYE_BUFFER_MAX_SIZE = 50;
export const BIRD_EYE_DEBUG_ON = false;

export interface BirdsEyeLocation {
  latitude: number;
  longitude: number;
  bearing: number;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export abstract class BirdsViewService {
  private _locationSubject: Subject<any> = new Subject();

  protected currentLocation: BirdsEyeLocation = null;
  protected nextLocation: BirdsEyeLocation = null;
  protected bufferedLocations: BirdsEyeLocation[] = [];
  protected bufferReady: boolean = false;
  protected bufferInterval = null;
  protected currentBufferIntevalTime = BIRD_EYE_SOCKET_DELAY;
  private bufferIntervalCheckCounter = 10;
  protected pingInterval = null;
  protected firstMove = true;
  protected socket: any;
  protected wsMessagesTimestamps: number[] = [];
  protected tokenService = inject(TokenStorageService);

  public isConnected$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public get isConnected(): boolean {
    return this.isConnected$.value;
  }
  protected set isConnected(value) {
    if(value) {
      this._locationSubject = new Subject();
    }
    this.isConnected$.next(value);
  }

  protected locationSubjectObservable(): Observable<any> {
    return this._locationSubject.asObservable();
  }

  protected locationSubjectEmit(location: any): void {
    this._locationSubject.next(location);
  }

  protected locationSubjectEmitError(error: any): void {
    const uiError = BIRD_EYE_WS_UI_ERRORS_MAP.get(error);
    this._locationSubject.error(uiError);
  }

  public getVehicleLocationObservable(): Observable<any> {
    return this.locationSubjectObservable();
  }

  public abstract connectToSocket(driverId: string): void;

  public abstract disconnectFromSocket(): void;

  public abstract toggleVehicleBirdsView(driverId: string, vehicle?: any): void;

  protected abstract pingOkStatus(message: string): void;

  protected abstract bufferLocation(location: BirdsEyeLocation): void;

  protected abstract sendFromBuffer(): void;

  public getRotatedMarkerIcon(rotation: number): google.maps.ReadonlyIcon {
    const iconWidth=20;
    const iconHeight=20;
    const rotatedSvg = `
    <svg version="1.2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${iconWidth} ${iconHeight}" width="${iconWidth}" height="${iconHeight}">
      <style>
        .s0 { fill: #13cb52;stroke: #ffffff;stroke-linejoin: round;stroke-width: 1.5 }
      </style>
      <path class="s0" d="m2.5 18.2l7-17 7 17-7-3.5z" transform="rotate(${rotation}, ${iconWidth/2}, ${iconHeight/2})"/>
    </svg>
    `;

    const icon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(rotatedSvg),
      scaledSize: new google.maps.Size(iconWidth, iconHeight), // Icon size
      anchor: new google.maps.Point(iconWidth/2, iconHeight/2),   // Center the anchor
    };

    return icon;
  }

  public calculateIntermediateBearing(startBearing: number, endBearing: number, currentBearing: number): number {
    let newBearing = endBearing;
    const diffBearing = Math.abs(endBearing - startBearing) % 360;
    let deltaBearing = parseFloat((diffBearing / BIRD_EYE_LOCATION_INTER_POINTS).toFixed(1));
    if(diffBearing > 180) {
      deltaBearing = parseFloat(((Math.min(startBearing, endBearing) + 360 - Math.max(startBearing, endBearing)) / BIRD_EYE_LOCATION_INTER_POINTS).toFixed(1));
    }

    if(
      (startBearing < endBearing && diffBearing < 180) ||
      (startBearing > endBearing && diffBearing > 180)
    ) {
      newBearing = parseFloat(((currentBearing + deltaBearing + 360) % 360).toFixed(1));
    } else {
      newBearing = parseFloat(((currentBearing - deltaBearing + 360) % 360).toFixed(1));
    }
    // console.log(`Bearing  S:${startBearing.toFixed(1)} E:${endBearing.toFixed(1)} D: ${diffBearing.toFixed(1)} - ${newBearing.toFixed(1)}`);

    return newBearing;
  }

  protected storeTimestamp(): void {
    this.wsMessagesTimestamps.push(Date.now()); // Add timestamp when message is received
    this.bufferIntervalCheckCounter++;
  }

  protected startBufferInterval(intervalTime: number, clearBuffer = false): void {
    // Clear the existing interval
    if (this.bufferInterval) {
      clearInterval(this.bufferInterval);
    }

    // Start a new interval
    this.bufferReady = false;
    if(clearBuffer) {
      this.bufferedLocations = [];
      this.wsMessagesTimestamps = [];
      this.bufferIntervalCheckCounter = 0;
    }
    this.bufferInterval = setInterval(() => {
      this.sendFromBuffer();
      if(this.bufferIntervalCheckCounter >= 10) {
        this.calculateBestBufferIntervalTime();
        this.startBufferInterval(this.currentBufferIntevalTime);
        this.bufferIntervalCheckCounter = 0;
      }

    }, intervalTime);
    this.currentBufferIntevalTime = intervalTime; // Update the current interval time
    if(BIRD_EYE_DEBUG_ON) {
      console.debug(`[BV] Buffer Interval set to ${intervalTime}ms`);
    }
}

  protected stopBufferInterval(clearBuffer = false): void {
    if (this.bufferInterval) {
      clearInterval(this.bufferInterval);
      this.bufferInterval = null;
      this.bufferReady = false;
      if(clearBuffer) {
        this.bufferedLocations = [];
        this.wsMessagesTimestamps = [];
        this.bufferIntervalCheckCounter = 0;
      }

      if(BIRD_EYE_DEBUG_ON) {
        console.debug("[BV] Buffer Interval stopped");
      }
    }
  }

  protected calculateBestBufferIntervalTime(): void {

    // Calculate intervals
    const intervals: number[] = this.wsMessagesTimestamps.slice(1).map((timestamp, i) => timestamp - this.wsMessagesTimestamps[i]);

    // Calculate mean interval
    const meanInterval: number = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    this.currentBufferIntevalTime = parseFloat(meanInterval.toFixed(1));
    if(this.wsMessagesTimestamps.length > 500) {
      this.wsMessagesTimestamps = this.wsMessagesTimestamps.splice(0,100);
    }
  }

  public getCurrentSocketDelay(): number {
    let delay = BIRD_EYE_SOCKET_DELAY;
    if(BIRD_EYE_BUFFER_ON) {
      delay = this.currentBufferIntevalTime;
    }

    return delay / BIRD_EYE_LOCATION_INTER_POINTS * BIRD_EYE_DELAY_OFFSET_FACTOR;
  }
}
